import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const SUBJECTS = [
  'General Enquiry',
  'Bug Report',
  'Feature Request',
  'Billing / Payment Issue',
  'Account Issue',
  'Other',
];

export default function ContactUsDialog({ open, onClose }) {
  const [subject, setSubject] = useState('General Enquiry');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/contact`,
        { subject, message },
        { withCredentials: true }
      );
      setSent(true);
    } catch {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSent(false);
      setSubject('General Enquiry');
      setMessage('');
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-[#0a0a0f] border border-white/10 text-white" data-testid="contact-us-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Contact Us</DialogTitle>
        </DialogHeader>

        {sent ? (
          <div className="flex flex-col items-center py-8 gap-4" data-testid="contact-success">
            <CheckCircle className="w-12 h-12 text-green-500" />
            <h3 className="text-lg font-medium text-white">Message Sent!</h3>
            <p className="text-white/50 text-sm text-center">
              We'll get back to you as soon as possible.
            </p>
            <Button
              onClick={handleClose}
              className="bg-[#FF4500] hover:bg-[#CC3700] text-white mt-2"
              data-testid="contact-done-btn"
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-white/70">Subject</Label>
              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full h-10 rounded-md border border-white/10 bg-black/50 px-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF4500]/40"
                data-testid="contact-subject-select"
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s} className="bg-[#121216]">{s}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Message</Label>
              <Textarea
                placeholder="How can we help?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="bg-black/50 border-white/10 text-white placeholder:text-white/30 min-h-[140px] resize-none focus:border-[#FF4500]/40"
                maxLength={5000}
                data-testid="contact-message-input"
              />
              <p className="text-xs text-white/30 text-right">{message.length}/5000</p>
            </div>

            <Button
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className="w-full bg-[#FF4500] hover:bg-[#CC3700] text-white disabled:opacity-40"
              data-testid="contact-send-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
